import React, { Fragment, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { RaisedButton, IconButton, Chip } from 'material-ui';
import CreateIcon from 'material-ui/svg-icons/content/create';
import DeleteIcon from 'material-ui/svg-icons/action/delete';
import AddIcon from 'material-ui/svg-icons/content/add';

import StevedoringPopover from './StevedoringPopover';
import { changePort } from 'actions/regions/ports';

import AddServiceProvider from './AddServiceProvider';
import EditServiceProvider from './EditServiceProvider';
import InfoIcon from 'material-ui/svg-icons/action/info-outline';
import { getRecommendedTitle } from 'helpers/ports';

const styles = {
    btn: {
        style: { margin: '20px 0 70px 0' },
        labelStyle: { textTransform: 'none', fontSize: '13px' },
    },
};

function ServiceProvider(props) {
    const { fields, serviceProviders } = props;

    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [current, setCurrent] = useState({ field: '' });

    const checkHasRecommended = () => {
        if (Array.isArray(serviceProviders)) {
            return serviceProviders.find(s => s.recommended && s.recommended.endAt);
        }

        return {};
    };

    const [hasRecommended, setHasRecommended] = useState(checkHasRecommended());

    useEffect(() => {
        setHasRecommended(checkHasRecommended());
    }, [serviceProviders]);

    const addServiceProvider = useCallback(() => {
        const emptyServiceProvider = {
            activities: [],
            name: undefined,
            address: undefined,
            PIC: undefined,
            emails: [undefined],
            phones: [{
                phone: undefined,
                code: undefined,
            }],
            recommended: { endAt: null, at: null, addedBy: null },
            companyId: undefined,
            limitations: [{
                maxDraught: undefined,
                maxBeam: undefined,
                maxLOA: undefined,
                maxDWT: undefined,
                channelDepth: undefined,
                anchorageDepth: undefined,
            }],
        };

        fields.push(emptyServiceProvider);
        setCurrent({ field: `${fields.name}[${fields.length}]` });
        setOpenAddDialog(true);
    }, [fields]);

    const toggleEditDialog = useCallback((c) => {
        if (c) setCurrent(c);
        setOpenEditDialog(prev => !prev);
    }, []);

    const toggleAddDialog = useCallback((c) => {
        if (c) setCurrent(c);
        setOpenAddDialog(prev => !prev);
    }, []);

    return (
        <Fragment>
            <div className='table'>
                <div className='table-header'>
                    <div className='table-row'>
                        <div className='table-col'>
                            Activities
                        </div>
                        <div className='table-col'>
                            Company Name
                        </div>
                        <div className='table-col'>
                            Contact Details
                        </div>
                    </div>
                </div>

                <div className="table-body">
                    {
                        fields.map((field, index) => {
                            if (!serviceProviders[index]) {
                                return null;
                            }

                            return (
                                <div className="table-row" key={index}>
                                    <div className="table-col info">
                                        <div className='name'>
                                            {
                                                serviceProviders[index].activities && serviceProviders[index].activities.includes('Stevedoring Company') ?
                                                    <StevedoringPopover
                                                        serviceProvider={serviceProviders[index]}
                                                    />
                                                    :
                                                    <div>
                                                        {
                                                            serviceProviders[index].activities.map((activity, idx) => (
                                                                <div key={`activity_${idx}`}>
                                                                    <span>{activity}</span><br />
                                                                </div>
                                                            ))
                                                        }
                                                    </div>
                                            }
                                        </div>
                                    </div>
                                    <div className='table-col'>
                                        <div className='name company'>
                                            {serviceProviders[index].name}
                                        </div>
                                    </div>
                                    <div className='table-col'>
                                        <div className='list'>
                                            {
                                                serviceProviders[index].emails.map((email, emailIndex) => (
                                                    <div key={emailIndex} className='email'>{email}</div>
                                                ))
                                            }
                                        </div>

                                        <div className='list'>
                                            {
                                                serviceProviders[index].phones.map((phone, phoneIndex) => (
                                                    <div key={phoneIndex} className='phone'>{`${phone.code} ${phone.phone}`}</div>
                                                ))
                                            }
                                        </div>

                                        <div className='name'>
                                            {serviceProviders[index].address}
                                        </div>

                                        <div className='name'>
                                        PIC: {serviceProviders[index].PIC}
                                        </div>
                                    </div>
                                    <div
                                        className='table-col edit'
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            position: 'relative',
                                            width: '122px',
                                        }}
                                    >
                                        {serviceProviders[index].recommended && serviceProviders[index].recommended.endAt ? (
                                            <Chip
                                                style={{
                                                    backgroundColor: '#E8F5E9',
                                                    height: 24,
                                                    position: 'absolute',
                                                    top: 10,
                                                    right: 0,
                                                    zIndex: 1000,
                                                    width: 118,
                                                }}
                                                labelStyle={{ 
                                                    color: '#2E7D32', 
                                                    lineHeight: '24px', 
                                                    fontSize: '12px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                Recommended
                                                <IconButton
                                                    tooltip={getRecommendedTitle(serviceProviders[index])}
                                                    tooltipPosition='bottom-left'
                                                    style={{
                                                        width: 20,
                                                        height: 18,
                                                        padding: 0,
                                                    }}
                                                    iconStyle={{ width: 16, height: 16, color: '#2E7D32' }}
                                                >
                                                    <InfoIcon />
                                                </IconButton>
                                            </Chip>
                                        ) : null}

                                        <div className='icon edit-icon'>
                                            <IconButton
                                                iconStyle={{ color: '#CBCBCB' }}
                                                onClick={() => {
                                                    toggleEditDialog({ field });
                                                }}
                                            >
                                                <CreateIcon />
                                            </IconButton>
                                        </div>

                                        <div className='icon delete-icon'>
                                            <IconButton
                                                iconStyle={{ color: '#CBCBCB' }}
                                                onClick={() => {
                                                    fields.remove(index);
                                                }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    }
                </div>
            </div>

            <RaisedButton
                label='Add Service Provider'
                labelStyle={styles.btn.labelStyle}
                style={styles.btn.style}
                labelPosition='before'
                icon={<AddIcon />}
                primary
                onClick={addServiceProvider}
            />

            <EditServiceProvider
                open={openEditDialog}
                toggle={toggleEditDialog}
                field={current.field}
                hasRecommended={hasRecommended}
            />

            <AddServiceProvider
                open={openAddDialog}
                toggle={toggleAddDialog}
                field={current.field}
                fields={fields}
                hasRecommended={hasRecommended}
            />
        </Fragment>
    );
}

ServiceProvider.propTypes = {
    port: PropTypes.object,
    fields: PropTypes.object,
    serviceProviders: PropTypes.array,
};

ServiceProvider.defaultProps = {
    port: {},
    fields: {},
    serviceProviders: [],
};

export default connect(
    (state, ownProps) => ({
        ...ownProps,
        serviceProviders: state.port.serviceProviders,
    }),
    dispatch => bindActionCreators({
        changePort,
    }, dispatch),
)(ServiceProvider);



// WEBPACK FOOTER //
// ./app/components/Regions/Port/ServiceProviders/ServiceProvider.jsx